import { z } from 'zod';
import { defineTool, STAFF } from './define.js';
import { IsoDate, IsoDateTime } from '../types/common.js';
import { AppointmentSchema, AppointmentStatusSchema, FollowUpRequestSchema, ProviderScheduleSchema, SlotSchema } from '../types/scheduling.js';
import { PendingActionSchema } from '../types/session.js';
import { ProviderSchema } from '../types/tenant.js';

const STAFF_AND_PATIENT = [...STAFF, 'PATIENT'] as const;

export const AppointmentViewSchema = AppointmentSchema.extend({ patientName: z.string().optional(), providerName: z.string().optional() });

export const listProviders = defineTool({
  name: 'listProviders',
  description: 'List clinicians/providers in this clinic that can be scheduled. Use it to resolve "Dr. Ahmed" to a providerId.',
  input: z.object({ query: z.string().optional() }),
  output: z.object({ providers: z.array(ProviderSchema) }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: false, write: false, exposeToLlm: true,
});

export const getAppointments = defineTool({
  name: 'getAppointments',
  description: 'List appointments. Filter by date range, provider, status, or the active patient. Front desk sees the whole clinic; patients see only their own.',
  input: z.object({
    from: IsoDate.optional().describe('Local date, inclusive. Defaults to today'),
    to: IsoDate.optional().describe('Local date, inclusive. Defaults to from'),
    providerId: z.string().optional(),
    status: z.array(AppointmentStatusSchema).optional(),
    /** When true, restrict to the active patient (physician asking "his next appointment"). */
    forActivePatient: z.boolean().default(false),
  }),
  output: z.object({ appointments: z.array(AppointmentViewSchema), timeZone: z.string() }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: false, write: false, exposeToLlm: true,
});

export const findAvailableSlots = defineTool({
  name: 'findAvailableSlots',
  description: 'Compute open appointment slots from provider schedules minus booked appointments. Supports "earliest with Dr X next week" and "in three months" via from/to dates.',
  input: z.object({
    providerId: z.string().optional().describe('Omit for any provider'),
    appointmentTypeCode: z.string().optional().describe('Defaults to FOLLOWUP for existing patients, NEW otherwise'),
    from: IsoDate,
    to: IsoDate.optional().describe('Defaults to from + 7 days'),
    preferredTime: z.string().regex(/^\d{2}:\d{2}$/).optional(),
    limit: z.number().int().min(1).max(50).default(8),
  }),
  output: z.object({ slots: z.array(SlotSchema), searched: z.object({ from: IsoDate, to: IsoDate, providerIds: z.array(z.string()) }) }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: false, write: false, exposeToLlm: true,
});

export const createAppointmentDraft = defineTool({
  name: 'createAppointmentDraft',
  description: 'Hold a specific slot for the active patient (or a given patientId for front desk) as a DRAFT and return a pending action. The slot is reserved atomically; it is booked only after confirmation.',
  input: z.object({
    patientId: z.string().optional(),
    providerId: z.string(),
    start: IsoDateTime,
    appointmentTypeCode: z.string(),
    reason: z.string().max(300).optional(),
  }),
  output: z.object({ pendingAction: PendingActionSchema, appointment: AppointmentViewSchema }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: false, write: true, exposeToLlm: true, audit: 'APPOINTMENT_DRAFT',
});

export const confirmAppointment = defineTool({
  name: 'confirmAppointment',
  description: 'Confirm a pending APPOINTMENT draft, turning the held slot into a BOOKED appointment. Idempotent.',
  input: z.object({ pendingActionId: z.string() }),
  output: z.object({ appointment: AppointmentViewSchema, replayed: z.boolean() }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: false, write: true, exposeToLlm: true, audit: 'APPOINTMENT_BOOK',
});

export const cancelAppointmentDraft = defineTool({
  name: 'cancelAppointmentDraft',
  description: 'Prepare cancellation of a booked appointment; returns a pending action.',
  input: z.object({ appointmentId: z.string(), reason: z.string().max(300).optional() }),
  output: z.object({ pendingAction: PendingActionSchema }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: false, write: true, exposeToLlm: true, audit: 'APPOINTMENT_CANCEL',
});

export const rescheduleAppointmentDraft = defineTool({
  name: 'rescheduleAppointmentDraft',
  description: 'Hold a new slot for an existing appointment; on confirmation the old slot is released and the new one booked atomically.',
  input: z.object({ appointmentId: z.string(), providerId: z.string().optional(), start: IsoDateTime }),
  output: z.object({ pendingAction: PendingActionSchema }),
  roles: STAFF_AND_PATIENT, requiresPatientContext: false, write: true, exposeToLlm: true, audit: 'APPOINTMENT_RESCHEDULE',
});

export const checkInAppointment = defineTool({
  name: 'checkInAppointment',
  description: 'Mark a booked appointment as CHECKED_IN (patient has arrived). Does not change the physician patient context.',
  input: z.object({ appointmentId: z.string() }),
  output: z.object({ appointment: AppointmentViewSchema }),
  roles: STAFF, requiresPatientContext: false, write: true, exposeToLlm: true, audit: 'APPOINTMENT_CHECKIN',
});

export const markNoShow = defineTool({
  name: 'markNoShow',
  description: 'Mark a booked appointment as NO_SHOW.',
  input: z.object({ appointmentId: z.string() }),
  output: z.object({ appointment: AppointmentViewSchema }),
  roles: STAFF, requiresPatientContext: false, write: true, exposeToLlm: true, audit: 'APPOINTMENT_NOSHOW',
});

export const proposeFollowUp = defineTool({
  name: 'proposeFollowUp',
  description: 'Physician proposes a follow-up for the active patient without booking (e.g. "follow up in three months"). Creates an OPEN follow-up request that front desk or the patient can book later.',
  input: z.object({
    intervalDays: z.number().int().min(1).max(730).optional(),
    targetDate: IsoDate.optional(),
    appointmentTypeCode: z.string().default('FOLLOWUP'),
    providerId: z.string().optional().describe('Defaults to the requesting physician'),
    reason: z.string().max(300).optional(),
  }),
  output: z.object({ followUp: FollowUpRequestSchema, suggestedSlots: z.array(SlotSchema) }),
  roles: STAFF, requiresPatientContext: true, write: true, exposeToLlm: true, audit: 'APPOINTMENT_DRAFT',
});

export const getProviderSchedule = defineTool({
  name: 'getProviderSchedule',
  description: 'Weekly availability template for a provider.',
  input: z.object({ providerId: z.string() }),
  output: z.object({ schedule: ProviderScheduleSchema.nullable() }),
  roles: STAFF, requiresPatientContext: false, write: false, exposeToLlm: true,
});
